import {GoogleMapsContext, useMapsLibrary} from "@vis.gl/react-google-maps";
import {forwardRef, type Ref, useContext, useEffect, useImperativeHandle, useRef} from "react";

//FIXME: Once to the component ready in vis.gl library, use instead (~24Q2)

type PolylineEventProps = {
    onClick?: (e: google.maps.MapMouseEvent) => void;
    onDrag?: (e: google.maps.MapMouseEvent) => void;
    onDragStart?: (e: google.maps.MapMouseEvent) => void;
    onDragEnd?: (e: google.maps.MapMouseEvent) => void;
    onMouseOver?: (e: google.maps.MapMouseEvent) => void;
    onMouseOut?: (e: google.maps.MapMouseEvent) => void;
};

type PolylineCustomProps = {
    encodedPaths?: string[];
};

export type PolylineProps = google.maps.PolylineOptions & PolylineEventProps & PolylineCustomProps;

export type PolylineRef = Ref<google.maps.Polyline | null>;

function usePolyline(props: PolylineProps) {
    const {
        onClick,
        onDrag,
        onDragStart,
        onDragEnd,
        onMouseOver,
        onMouseOut,
        encodedPaths,
        ...polylineOptions
    } = props;
    // Avoid triggering the useEffect when the callbacks change (if not memoized)
    const callbacks = useRef<Record<string, (e: unknown) => void>>({});

    useEffect(() => {
        Object.assign(callbacks.current, {
            onClick,
            onDrag,
            onDragStart,
            onDragEnd,
            onMouseOver,
            onMouseOut,
        });
    });

    const geometryLibrary = useMapsLibrary("geometry");

    // Use lazy initialization pattern to satisfy React Compiler
    const polylineRef = useRef<google.maps.Polyline | null>(null);
    const getPolyline = () => {
        if (!polylineRef.current) {
            polylineRef.current = new google.maps.Polyline();
        }
        return polylineRef.current;
    };

    const map = useContext(GoogleMapsContext)?.map;

    useEffect(() => {
        const polyline = getPolyline();
        polyline.setOptions(polylineOptions);
    }, [polylineOptions]);

    useEffect(() => {
        if (!encodedPaths || !geometryLibrary) return;
        const path = encodedPaths.at(0);
        if (!path) return;
        const polyline = getPolyline();
        const paths = geometryLibrary.encoding.decodePath(path);
        polyline.setPath(paths);
    }, [encodedPaths, geometryLibrary]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const polyline = getPolyline();
        polyline.setMap(map);

        return () => {
            polyline.setMap(null);
        };
    }, [map]);

    useEffect(() => {
        const polyline = getPolyline();

        const gme = google.maps.event;
        [
            ["click", "onClick"],
            ["drag", "onDrag"],
            ["dragstart", "onDragStart"],
            ["dragend", "onDragEnd"],
            ["mouseover", "onMouseOver"],
            ["mouseout", "onMouseOut"],
        ].forEach(([eventName, eventCallback]) => {
            if (eventName && eventCallback) {
                gme.addListener(polyline, eventName, (e: google.maps.MapMouseEvent) => {
                    const callback = callbacks.current[eventCallback];
                    if (callback) callback(e);
                });
            }
        });

        return () => {
            gme.clearInstanceListeners(polyline);
        };
    }, []);

    return polylineRef;
}

export const Polyline = forwardRef(function Polyline(props: PolylineProps, ref: PolylineRef) {
    const polylineRef = usePolyline(props);

    useImperativeHandle(ref, () => {
        // Ensure polyline is created before exposing via ref
        if (!polylineRef.current) {
            polylineRef.current = new google.maps.Polyline();
        }
        return polylineRef.current;
    }, [polylineRef]);

    return null;
});
